import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, ChevronRight, ArrowLeft, X, Languages, Coins } from "lucide-react";
import { COUNTRIES, type CountryCode } from "@/lib/ecom/catalog";
import { useCurrency } from "@/lib/ecom/currency";
import { LANGUAGES, languageByCode } from "@/lib/ecom/languages";
import { swapLocalePath, type Locale, LOCALES } from "@/lib/i18n/locale";
import { Flag } from "./Flag";

type View = "summary" | "language" | "currency";

const LANG_FLAG: Record<string, string> = { en: "US", vi: "VN", el: "GR" };
const langFlagCode = (code: string) => LANG_FLAG[code] ?? code.toUpperCase();

export function CountryPicker() {
  const { country, setCountry, language, setLanguage } = useCurrency();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>("summary");
  const [pendingLang, setPendingLang] = useState(language);
  const [pendingCountry, setPendingCountry] = useState(country.code);
  const ref = useRef<HTMLDivElement>(null);
  const currentLang = languageByCode(language);

  useEffect(() => {
    if (!open) return;
    setPendingLang(language);
    setPendingCountry(country.code);
  }, [open, language, country.code]);

  const close = () => {
    setOpen(false);
    setTimeout(() => setView("summary"), 150);
  };

  const save = () => {
    const langChanged = pendingLang !== language;
    if (pendingCountry !== country.code) {
      setCountry(pendingCountry as CountryCode);
    }
    if (langChanged) {
      setLanguage(pendingLang);
    }
    close();

    if (langChanged && typeof window !== "undefined" && (LOCALES as string[]).includes(pendingLang)) {
      const target = swapLocalePath(window.location.pathname, pendingLang as Locale);
      if (target !== window.location.pathname) {
        window.location.href = target + window.location.search + window.location.hash;
      }
    }
  };

  const pickLanguage = (code: string) => {
    setPendingLang(code);
    // Auto-select the home country/currency for locales with one.
    if (code === "vi") {
      setPendingCountry("VN" as CountryCode);
    } else if (code === "el") {
      setPendingCountry("GR" as CountryCode);
    }
    setView("summary");
  };

  const pickCountry = (code: CountryCode) => {
    setPendingCountry(code);
    setView("summary");
  };

  const pendingLangObj = languageByCode(pendingLang);
  const pendingCountryObj = COUNTRIES.find((c) => c.code === pendingCountry) ?? country;
  const hasChanges = pendingLang !== language || pendingCountry !== country.code;

  const summary = (
    <div className="p-2">
      <p className="px-3 pb-2 pt-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        Preferences
      </p>

      <button
        type="button"
        onClick={() => setView("language")}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-muted"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted">
          <Languages className="h-4 w-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Language
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <Flag code={langFlagCode(pendingLangObj.code)} fallback={pendingLangObj.flag} className="h-4 w-4" />
            <span className="truncate text-[13.5px] font-semibold leading-tight">{pendingLangObj.native}</span>
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          Change <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </button>

      <button
        type="button"
        onClick={() => setView("currency")}
        className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-muted"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted">
          <Coins className="h-4 w-4" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[10.5px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Currency
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <Flag code={pendingCountryObj.code} fallback={pendingCountryObj.flag} className="h-4 w-4" />
            <span className="truncate text-[13.5px] font-semibold leading-tight">
              {pendingCountryObj.currency} · {pendingCountryObj.symbol}
            </span>
            <span className="truncate text-[12px] text-muted-foreground">— {pendingCountryObj.name}</span>
          </span>
        </span>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          Change <ChevronRight className="h-3.5 w-3.5" />
        </span>
      </button>
    </div>
  );

  const SubHeader = ({ title }: { title: string }) => (
    <div className="flex items-center gap-1 border-b border-foreground/10 px-2 py-2">
      <button
        type="button"
        onClick={() => setView("summary")}
        className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-semibold transition hover:bg-muted"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>
      <p className="ml-1 text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        {title}
      </p>
    </div>
  );

  const languageList = (
    <>
      <SubHeader title="Choose language" />
      <div className="max-h-[55vh] overflow-auto p-1.5">
        {LANGUAGES.map((l) => {
          const active = l.code === pendingLang;
          return (
            <button
              key={l.code}
              role="option"
              aria-selected={active}
              onClick={() => pickLanguage(l.code)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition hover:bg-muted ${active ? "bg-muted" : ""}`}
            >
              <Flag code={langFlagCode(l.code)} fallback={l.flag} className="h-5 w-5" />
              <span className="flex-1 min-w-0">
                <span className="block truncate text-[13px] font-semibold leading-tight">{l.native}</span>
                <span className="block text-[11px] text-muted-foreground">{l.name} · {l.code.toUpperCase()}</span>
              </span>
              {active && <Check className="h-4 w-4 text-foreground" />}
            </button>
          );
        })}
      </div>
    </>
  );

  const currencyList = (
    <>
      <SubHeader title="Ship to & price in" />
      <div className="max-h-[55vh] overflow-auto p-1.5">
        {COUNTRIES.map((c) => {
          const active = c.code === pendingCountry;
          return (
            <button
              key={c.code}
              role="option"
              aria-selected={active}
              onClick={() => pickCountry(c.code as CountryCode)}
              className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition hover:bg-muted ${active ? "bg-muted" : ""}`}
            >
              <Flag code={c.code} fallback={c.flag} className="h-5 w-5" />
              <span className="flex-1 min-w-0">
                <span className="block truncate text-[13px] font-semibold leading-tight">{c.name}</span>
                <span className="block text-[11px] text-muted-foreground">{c.currency} · {c.symbol}</span>
              </span>
              {active && <Check className="h-4 w-4 text-foreground" />}
            </button>
          );
        })}
      </div>
    </>
  );

  const panel =
    view === "language" ? languageList : view === "currency" ? currencyList : summary;

  const modal = open ? (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-md" aria-hidden="true" onClick={close} />
      <div
        role="dialog"
        aria-label="Language and currency"
        className="relative z-10 flex max-h-[85vh] w-full max-w-sm flex-col overflow-hidden rounded-2xl bg-card animate-in fade-in zoom-in-95"
      >
        <div className="flex items-center justify-between border-b border-foreground/10 px-4 py-3">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
            {view === "summary" ? "Preferences" : view === "language" ? "Language" : "Currency"}
          </p>
          <button
            type="button"
            onClick={close}
            className="rounded-full p-1.5 transition hover:bg-muted"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="overflow-auto">{panel}</div>

        <div className="flex items-center justify-end gap-2 border-t border-foreground/10 px-4 py-3">
          <button
            type="button"
            onClick={close}
            className="rounded-xl px-4 py-2 text-[12.5px] font-semibold text-muted-foreground transition hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={!hasChanges}
            className={`rounded-xl px-4 py-2 text-[12.5px] font-semibold text-primary-foreground transition ${
              hasChanges
                ? "bg-foreground hover:bg-foreground/90"
                : "bg-foreground/30 cursor-not-allowed"
            }`}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        className="inline-flex h-8 items-center gap-1.5 rounded-full border border-foreground/10 bg-card px-2.5 text-[11.5px] font-semibold leading-none text-foreground transition hover:border-foreground/30"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Flag code={langFlagCode(currentLang.code)} fallback={currentLang.flag} className="h-4 w-4" />
        <span className="leading-none uppercase">{currentLang.code}</span>
        <span className="hidden sm:inline opacity-30 leading-none">·</span>
        <span className="hidden sm:inline tabular-nums leading-none">{country.currency}</span>
        <ChevronDown className={`h-3 w-3 opacity-60 transition ${open ? "rotate-180" : ""}`} />
      </button>

      {open && typeof document !== "undefined" ? createPortal(modal, document.body) : null}
    </div>
  );
}
